import React, { useState, useEffect } from 'react';
import FormInput from '../../common/FormInput';
import FormSelect from '../../common/FormSelect';
import Button from '../../common/Button';
import './styles.css';

/**
 * 用戶表單組件
 * 用於新增和編輯用戶
 * 
 * @param {Object} props - 組件屬性
 * @param {Object} props.user - 用戶對象（編輯模式下提供）
 * @param {Function} props.onSubmit - 表單提交回調
 * @param {Function} props.onCancel - 取消回調
 * @param {string} props.submitText - 提交按鈕文本
 * @param {string} props.message - 表單消息
 * @param {string} props.messageType - 表單消息類型 (error, success)
 * @param {boolean} props.loading - 加載狀態
 * @returns {JSX.Element} 用戶表單組件
 */
function UserForm({
    user = null,
    onSubmit,
    onCancel,
    submitText = '提交',
    message = '',
    messageType = '',
    loading = false
}) {
    // 表單狀態
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [role, setRole] = useState('normal');
    const [errors, setErrors] = useState({});

    // 角色選項
    const roleOptions = [
        { value: 'normal', label: '普通用戶' },
        { value: 'admin', label: '管理員' }
    ];

    // 當用戶對象變更時，更新表單
    useEffect(() => {
        if (user) {
            setUsername(user.username || '');
            setPassword(''); // 不顯示密碼
            setRole(user.role || 'normal');
        } else {
            setUsername('');
            setPassword('');
            setRole('normal');
        }
        setErrors({});
    }, [user]);

    // 驗證密碼
    const validatePassword = (password) => {
        const errors = {};

        if (user && !password) {
            // 編輯模式下，密碼可以為空
            return errors;
        }

        if (!user && !password) {
            errors.password = '請輸入密碼';
        } else if (password && password.length < 6) {
            errors.password = '密碼長度必須至少為6個字符';
        } else if (password && !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
            errors.password = '密碼必須包含至少一個大寫字母、一個小寫字母和一個數字';
        }

        return errors;
    };

    // 驗證表單
    const validateForm = () => {
        const newErrors = {};

        if (!username) {
            newErrors.username = '請輸入用戶名';
        }

        const passwordErrors = validatePassword(password);
        if (Object.keys(passwordErrors).length > 0) {
            newErrors.password = passwordErrors.password;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // 處理表單提交
    const handleSubmit = (e) => {
        e.preventDefault();

        if (validateForm()) {
            onSubmit({
                id: user?.id,
                username,
                password,
                role
            });
        }
    };

    return (
        <form onSubmit={handleSubmit} className="user-form">
            <FormInput
                id="username"
                label="用戶名"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="請輸入用戶名"
                required
                error={errors.username}
            />

            <FormInput
                id="password"
                label={user ? '密碼（留空則不修改）' : '密碼'}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={user ? '請輸入新密碼' : '請輸入密碼'}
                required={!user}
                error={errors.password}
                helpText="密碼必須至少6個字符，包含大小寫字母和數字"
            />

            <FormSelect
                id="role"
                label="角色"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                options={roleOptions}
            />

            {message && (
                <div className={`form-message ${messageType === 'error' ? 'error' : 'success'}`}>
                    {message}
                </div>
            )}

            <div className="form-actions">
                <Button
                    type="button"
                    variant="secondary"
                    onClick={onCancel}
                    disabled={loading}
                >
                    取消
                </Button>
                <Button
                    type="submit"
                    variant="primary"
                    disabled={loading}
                >
                    {loading ? '處理中...' : submitText}
                </Button>
            </div>
        </form>
    );
}

export default UserForm; 